import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {interval, Observable, tap, of} from 'rxjs';
import {filter, map, switchMap, first} from "rxjs/operators";
import {CoreConfig} from "../core.config";
import {ProfileService} from "./profile.service";
import {SubscriptionService} from "./subscription.service";
import {FavoriteService} from "./favorite.service";
import {ActivatedRoute, Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  constructor(
    private http: HttpClient,
    private profileService: ProfileService,
    private subscriptionService: SubscriptionService,
    private favoriteService: FavoriteService,
    public coreConfig: CoreConfig) {
  }

  login(username: string, password: string): Observable<boolean> {
    const formData = new FormData();
    formData.append('Username', username);
    formData.append('Password', password);

    return this.http.post(`${this.coreConfig.api.baseUrl}/names.nsf?Login`, formData, {
      withCredentials: true,
      responseType: 'text',
    }).pipe(
      switchMap(() => this.profileService.updateLoginStatus()),
      tap(() => this.loadUserData(true)),
    );
  }

  logout(): Observable<any> {
    return this.http.get(`${this.coreConfig.api.baseUrl}/names.nsf?logout`, {
      withCredentials: true,
      responseType: 'text',
    }).pipe(
      switchMap(() => this.profileService.updateLoginStatus()),
    );
  }


  private loadUserData(force: boolean = false) {
    // make sure its loaded
    this.subscriptionService.initialLoad(force);
    this.favoriteService.initialLoad(force);
  }

  pollLoginStatus() {
    // initial load
    this.profileService.updateLoginStatus(true).subscribe();

    // while this call might not be needed in case the user is a guest, it will speed up the process for logged in users
    // this will currently benefit e-dok users greatly. 5 Mar 2025
    this.loadUserData();

    // poll every 2 minutes
    interval(1000 * 60 * 2).pipe(
      filter(() => this.profileService.profileStateService.isLoggedIn()),
      switchMap(() => this.profileService.updateLoginStatus())
    ).subscribe();
  }

  public redirectToLoginIfRequired(redirectReason: string): Observable<void> {
    const requireLogin = this.activatedRoute.snapshot.queryParamMap.get('requirelogin') === '1';

    if (!requireLogin) {
      return of(undefined);
    }

    return this.profileService.profileStateService.profile$.pipe(
      first(),
      tap(profile => {
        if (!profile) {
          this.router.navigate(['auth/login'], {
            queryParams: {
              returnUrl: this.router.url,
              reason: redirectReason
            },
            replaceUrl: true
          });
        }
      }),
      map(() => undefined)
    );
  }


  private pendingUrlKey = 'pendingUrlRedirect';
  isPendingUrlRedirect() {
    return window.sessionStorage.getItem(this.pendingUrlKey) !== null;
  }

  redirectToPendingUrl() {
    const pendingUrl = window.sessionStorage.getItem(this.pendingUrlKey);
    if (pendingUrl) {
      window.sessionStorage.removeItem(this.pendingUrlKey);
      this.router.navigateByUrl(pendingUrl);
    }
  }

  setPendingUrlRedirect(returnUrl: string) {
    window.sessionStorage.setItem(this.pendingUrlKey, returnUrl);
  }
}
